import { Elements } from '@stripe/react-stripe-js'
import { Button } from '@viastud/ui/button'
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@viastud/ui/dialog'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { trpc } from '@viastud/ui/lib/trpc'
import type { UserDto } from '@viastud/ui/shared/edit-profile'
import { Minus, Plus, X } from 'lucide-react'
import React, { useState } from 'react'

import PaymentForm from '@/components/payment-form.tsx'
import { getStripe } from '@/lib/stripe'

interface BuyLessonsModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  child: UserDto
}

export function BuyLessonsModal({ isOpen, onOpenChange, child }: BuyLessonsModalProps) {
  const UNITARY_PRICE = 19.99
  const [quantity, setQuantity] = useState(1)

  React.useEffect(() => {
    if (isOpen) {
      setQuantity(1)
    }
  }, [isOpen])
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  const { toast } = useToast()

  const { mutateAsync: createPaymentIntent, isPending } =
    trpc.payment.parentSingleLessonPaymentIntent.useMutation()

  const handleQuantityChange = (delta: number) => {
    setQuantity((prev) => Math.max(0, prev + delta))
  }

  const totalQuantity = quantity

  const handleBuy = async () => {
    if (totalQuantity <= 0) {
      toast({
        title: 'Attention',
        description: 'Veuillez sélectionner au moins un cours.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await createPaymentIntent({
        children: [
          {
            id: child.id,
            quantity,
          },
        ],
      })

      setClientSecret(result.clientSecret)
      setIsPaymentModalOpen(true)
      onOpenChange(false)
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Erreur',
          description: error.message ?? 'Une erreur est survenue lors de la création du paiement.',
          variant: 'destructive',
        })
      }
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="fixed left-1/2 top-1/2 flex w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col gap-6 rounded-2xl bg-white px-8 py-10 shadow-xl">
          <DialogHeader className="flex flex-row items-start justify-between p-0">
            <div className="flex flex-col gap-1">
              <DialogTitle className="text-2xl font-semibold text-gray-950 sm:text-3xl">
                Acheter des cours
              </DialogTitle>
              <p className="text-gray-700">
                Sélectionnez le nombre de cours à l&#39;unité pour vos enfants.
              </p>
            </div>
            <DialogClose asChild>
              <Button variant="none" className="m-0 h-6 w-6 justify-start p-0">
                <X className="h-6 w-6" />
              </Button>
            </DialogClose>
          </DialogHeader>

          <div className="rounded-2xl border border-gray-300 px-6 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold">Cours à l’unité</h4>
                <p className="text-sm text-gray-500">Achetez des cours pour vos enfants.</p>
              </div>
              <div className="flex flex-col justify-end">
                <div className="text-3xl font-bold">
                  {UNITARY_PRICE.toFixed(2).replace('.', ',')}€
                </div>
                <div className="text-end text-sm font-normal text-gray-500">
                  <span>/ cours</span>
                </div>
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              <ul className="space-y-1">
                <li>✓ Crédit immédiat sur le compte</li>
                <li>✓ Valable pour tous les niveaux</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-gray-100 py-2">
              <div className="flex flex-col">
                <span className="text-base font-medium capitalize text-gray-900">
                  {child.firstName}
                </span>
                <span className="text-sm lowercase text-gray-500">{child.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  className="h-8 w-8 border border-gray-200 px-2"
                  onClick={() => {
                    handleQuantityChange(-1)
                  }}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-center text-4xl font-bold text-blue-600">
                  {quantity.toString().padStart(2, '0')}
                </span>
                <Button
                  className="h-8 w-8 px-2"
                  onClick={() => {
                    handleQuantityChange(1)
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <Button
            className="h-12 w-full rounded-full text-lg font-semibold"
            onClick={handleBuy}
            disabled={isPending}
          >
            {isPending
              ? 'Chargement...'
              : `Acheter pour ${(totalQuantity * UNITARY_PRICE).toFixed(2).replace('.', ',')} €`}
          </Button>
        </DialogContent>
      </Dialog>

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onOpenChange={setIsPaymentModalOpen}
        clientSecret={clientSecret}
        onClose={() => {
          setIsPaymentModalOpen(false)
        }}
        price={totalQuantity * UNITARY_PRICE}
        returnUrl={`${window.location.origin}/parent/settings`}
      />
    </>
  )
}

function PaymentModal({
  isOpen,
  onOpenChange,
  clientSecret,
  onClose,
  price,
  returnUrl,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  clientSecret: string | null
  onClose: () => void
  price: number | null
  returnUrl: string
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="fixed left-1/2 top-1/2 flex w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col gap-8">
        <DialogHeader className="flex flex-row items-end justify-between">
          <DialogTitle className="justify-between text-xl font-bold text-gray-950">
            {price ? `Acheter des cours pour ${price.toFixed(2)} €` : 'Acheter des cours'}
          </DialogTitle>
          <DialogClose asChild>
            <Button variant="none" className="m-0 h-6 w-6 justify-start p-0" onClick={onClose}>
              <X className="h-6 w-6" />
            </Button>
          </DialogClose>
        </DialogHeader>
        {!!clientSecret && (
          <Elements
            options={{
              clientSecret: clientSecret,
              appearance: {
                theme: 'flat',
                variables: {
                  colorPrimary: '#3347FF',
                  colorBackground: '#EBF4FF',
                  colorText: '#334155',
                  colorDanger: '#df1b41',
                  borderRadius: '16px',
                },
              },
              loader: 'auto',
            }}
            stripe={getStripe()}
          >
            <PaymentForm returnUrl={returnUrl} gtmEventType="achat_cours_unite" />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  )
}
